import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { UnitsCard, PropertyCardData } from '../../../../shared/components/units-card/units-card';
import { ApiProperty } from '../../../../core/models/api-property.model';
import { ApiService } from '../../../../core/services/api.service';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';
import { LanguageService } from '../../../../core/services/language.service';
import { Subscription } from 'rxjs';

export type FeaturedMode = 'featured' | 'rent' | 'sale';

interface FeaturedUnitsResponse {
  message: string;
  results: {
    data: ApiProperty[];
    pages: number;
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    nextPage: number | null;
    previousPage: number | null;
  };
}

const MODE_CONFIG: Record<
  FeaturedMode,
  { endpoint: string; titleKey: string; subtitleKey: string; viewAllRoute: string }
> = {
  featured: {
    endpoint: '/public/search/units-and-properties?isFeatured=true',
    titleKey: 'home.featuredUnits.title',
    subtitleKey: 'home.featuredUnits.subtitle',
    viewAllRoute: '/all-properties',
  },
  rent: {
    endpoint: '/public/search/units-and-properties?purpose=rent',
    titleKey: 'home.rentUnits.title',
    subtitleKey: 'home.rentUnits.subtitle',
    viewAllRoute: '/rent',
  },
  sale: {
    endpoint: '/public/search/units-and-properties?purpose=sale',
    titleKey: 'home.saleUnits.title',
    subtitleKey: 'home.saleUnits.subtitle',
    viewAllRoute: '/buy',
  },
};

@Component({
  selector: 'app-home-featured-units',
  templateUrl: './home-featured-units.html',
  styleUrl: './home-featured-units.scss',
  imports: [CommonModule, UnitsCard, TranslatePipe],
})
export class HomeFeaturedUnits implements OnInit, OnDestroy {
  @Input() mode: FeaturedMode = 'featured';
  @ViewChild('scrollTrack') scrollTrack!: ElementRef<HTMLElement>;

  properties: ApiProperty[] = [];
  isLoading = false;
  errorMessage = '';
  private subscription: Subscription | null = null;
  private autoScrollInterval: any;

  readonly SCROLL_AMOUNT = 420;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    this.loadUnits();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.stopAutoScroll();
  }

  get config() {
    return MODE_CONFIG[this.mode];
  }

  get isRtl(): boolean {
    return this.languageService.getCurrentLanguage() === 'ar';
  }

  scrollPrev(): void {
    const el = this.scrollTrack?.nativeElement;
    if (!el) return;
    el.scrollBy({
      left: this.isRtl ? this.SCROLL_AMOUNT : -this.SCROLL_AMOUNT,
      behavior: 'smooth',
    });
    this.startAutoScroll();
  }

  scrollNext(): void {
    const el = this.scrollTrack?.nativeElement;
    if (!el) return;
    el.scrollBy({
      left: this.isRtl ? -this.SCROLL_AMOUNT : this.SCROLL_AMOUNT,
      behavior: 'smooth',
    });
    this.startAutoScroll();
  }

  startAutoScroll(): void {
    this.stopAutoScroll();
    if (typeof window !== 'undefined') {
      this.autoScrollInterval = window.setInterval(() => {
        this.autoScrollNext();
      }, 1500);
    }
  }

  stopAutoScroll(): void {
    if (typeof window !== 'undefined' && this.autoScrollInterval) {
      window.clearInterval(this.autoScrollInterval);
    }
  }

  autoScrollNext(): void {
    const el = this.scrollTrack?.nativeElement;
    if (!el) return;
    
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) return;
    
    const currentScroll = Math.abs(el.scrollLeft);
    if (currentScroll >= maxScroll - 10) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      el.scrollBy({
        left: this.isRtl ? -this.SCROLL_AMOUNT : this.SCROLL_AMOUNT,
        behavior: 'smooth',
      });
    }
  }

  loadUnits(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.subscription = this.apiService.get<any>(this.config.endpoint).subscribe({
      next: (response) => {
        // Handle multiple possible response shapes defensively
        const data =
          response?.results?.data ?? // { results: { data: [] } }
          response?.data ?? // { data: [] }
          response?.properties?.data ?? // { properties: { data: [] } }
          (Array.isArray(response) ? response : []);

        this.properties = Array.isArray(data) ? data : [];
        this.isLoading = false;
        setTimeout(() => this.startAutoScroll(), 100);
      },
      error: () => {
        this.errorMessage = 'حدث خطأ أثناء تحميل الوحدات';
        this.isLoading = false;
      },
    });
  }

  // keep old name as alias so existing usages don't break
  loadFeaturedUnits(): void {
    this.loadUnits();
  }

  onPropertyClick(property: PropertyCardData): void {
    if ('targetId' in property && 'targetType' in property) {
      const p = property as ApiProperty;
      const targetId = p.targetId;
      const targetType = p.targetType;

      if (targetType === 'property' || targetType === 'project') {
        this.router.navigate(['/property', targetId], { state: { property } });
      } else if (targetType === 'unit') {
        const purpose = p.purpose;
        const priceType = p.priceType;

        if (purpose === 'sale_and_rent') {
          const route = priceType === 'daily' || priceType === 'monthly' ? '/rent' : '/buy';
          this.router.navigate([route, targetId], { state: { property } });
        } else if (purpose === 'rent') {
          this.router.navigate(['/rent', targetId], { state: { property } });
        } else {
          this.router.navigate(['/buy', targetId], { state: { property } });
        }
      }
    } else {
      const id = '_id' in property ? property._id : 'id' in property ? property.id : '';
      this.router.navigate(['/property', id], { state: { property } });
    }
  }

  viewAll(): void {
    this.router.navigate([this.config.viewAllRoute]);
  }
}
